import { Component } from "react"

import { isCancel } from "apisauce"
import PropTypes from "prop-types"

import { API } from "../Services/Api"

const withAuth = (WrappedComponent) => {
  class ComposedComponent extends Component {
    api = new API()

    // Auth endpoints resolve inside their own sagas (sign-in / refresh /
    // logout), so their errors must NOT re-enter the flow below.
    authEndpoints = ["auth/token/", "auth/logout/"]

    // Synchronous guard so a burst of 401s triggers the refresh/logout flow
    // only once. Reset on a successful token-endpoint response, or on any
    // accessToken change (see componentDidUpdate) so it never wedges after
    // sign-in, which bypasses this interceptor entirely. Not React state —
    // setState batching would let the whole burst through before it flips.
    handlingUnauthorized = false

    isAuthEndpoint = (url) =>
      typeof url === "string" && this.authEndpoints.some((endpoint) => url.includes(endpoint))

    isTokenEndpoint = (url) => typeof url === "string" && url.includes("auth/token/")

    requestInterceptor = this.api.interceptors.request.use((req) => {
      const { auth } = this.props

      req.headers.Authorization = auth?.accessToken ? `JWT ${auth?.accessToken}` : null
      if (
        ["auth/token/refresh/", "auth/token/verify/"].includes(req.url) &&
        req.headers.Authorization
      ) {
        delete req.headers.Authorization
      }

      return req
    })

    responseInterceptor = this.api.interceptors.response.use(
      (res) => {
        // Only a successful token endpoint means the session has recovered.
        // Other requests may finish while refresh is in flight and must not
        // unlock another refresh attempt.
        if (this.isTokenEndpoint(res?.config?.url)) {
          this.handlingUnauthorized = false
        }
        return res
      },
      (err) => {
        const { refreshToken, auth, logout, errorSave } = this.props
        const { status } = err.response || { status: null }
        const { url } = err.config || {}

        // A request its caller cancelled (a newer filter change) has no response either, but it is
        // no error: nothing to show.
        if (isCancel(err)) return Promise.reject(err)

        // Network / no-response errors: surface them, never touch the session.
        if (!status) {
          errorSave(err)
          return Promise.reject(err)
        }

        // Auth endpoints handle their own outcome; only a 401 on a normal
        // request means the access token expired and starts the flow.
        if (this.isAuthEndpoint(url) || status !== 401) {
          return Promise.reject(err)
        }

        // Handle the expired token once per burst: refresh if possible,
        // otherwise force logout so PrivateRoute redirects to /login.
        if (!this.handlingUnauthorized) {
          this.handlingUnauthorized = true
          if (auth?.refreshToken) {
            refreshToken(auth.refreshToken)
          } else {
            logout()
          }
        }

        return Promise.reject(err)
      }
    )

    componentDidUpdate(prevProps) {
      // A changed access token means a login/refresh succeeded somewhere —
      // including sign-in, which posts through a different axios instance
      // and never hits the interceptor above. Catching it here covers that
      // path too, so the guard can't stay wedged after a failed refresh.
      if (
        prevProps.auth?.accessToken !== this.props.auth?.accessToken &&
        this.props.auth?.accessToken
      ) {
        this.handlingUnauthorized = false
      }
    }

    componentWillUnmount() {
      this.api.interceptors.request.eject(this.requestInterceptor)
      this.api.interceptors.response.eject(this.responseInterceptor)
    }

    render() {
      return <WrappedComponent {...this.props} />
    }
  }

  ComposedComponent.propTypes = {
    refreshToken: PropTypes.func,
    logout: PropTypes.func,
    auth: PropTypes.shape({
      accessToken: PropTypes.string,
      refreshToken: PropTypes.string
    })
  }

  return ComposedComponent
}

export default withAuth
